import { BooleanInput } from '@angular/cdk/coercion';
import { CommonModule, NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { IEmpresa } from '@shared/models/Select/select';
import { AppAlertService } from '@shared/service/app/alert.service';
import { SelectsService } from '@shared/service/app/select.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    styleUrls      : ['./user.component.scss'],
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user',
    standalone     : true,
    imports        : [MatButtonModule, MatMenuModule, NgIf, MatIconModule, NgClass, MatDividerModule, CommonModule, RouterLink],
    providers      : [SelectsService]
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;

    empresas: IEmpresa[] = [];
    empresaActualId: number;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _selectService: SelectsService,
        private confirm: FuseConfirmationService,
        private alert: AppAlertService,
        private _authService: AuthService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    async ngOnInit() {
        const empresaCambiada = localStorage.getItem('empresaCambiada');

        if (empresaCambiada === 'true') {
            this.alert.success('Cambio de empresa realizado con éxito');
            localStorage.removeItem('empresaCambiada'); 
        }
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) =>
            {
                this.user = user;
                this.empresaActualId = user.idempresa;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
        this.empresas = await this._selectService.getSelectByModel<IEmpresa>(
            'Empresa',
            'idempresa',
            '{razon_social}'
        )
    }

    seleccionarEmpresa(empresa: IEmpresa): void {
        this.confirm.open({
            title: "Confirmar cambio de empresa",
            message: "¿Desea cambiar a " + empresa.descripcion + "? Se recargará la página para aplicar la empresa seleccionada.",
            dismissible: false,
            icon: {
                show: false
            },
            actions: {
                confirm: {
                    show: true,
                    color: 'primary',
                    label: 'Confirmar',
                },
                cancel: {
                    show: false,
                }
            },
        })
            .afterClosed()
            .subscribe(res => {
                if (res === 'confirmed') {
                    let payload = null;
                    payload = {
                        idempresa: empresa.id
                    }
                    this._userService.cambiarEmpresa(payload).subscribe({
                        next: (data) => {
                            // Actualiza el token con el cambio de empresa
                            this._authService.accessToken = data.token;
                            // Actualiza el idempresa del user seteado en el localstorage
                            const user = JSON.parse(localStorage.getItem('user') ?? '{}');
                            user.idempresa = data.result;

                            this._userService.user = user;
                            this._userService['_user'].next(user);

                            // Cambio para poder ver la empresa seteada que se cambio
                            this.empresaActualId = data.result;
                            
                            // Bandera para mostrar mensaje luego del reload
                            localStorage.setItem('empresaCambiada', 'true');

                            // Si estamos en pantallas de comprobantes o asociados, redirigir a cuotas antes de recargar
                            if (window.location.hash.includes('comprobante') || 
                                window.location.hash.includes('pago') || 
                                window.location.hash.includes('pagare') || 
                                window.location.hash.includes('asociados')) {
                                window.location.hash = '#/cuotas';
                            }

                            // Recargar la app
                            location.reload();
                        },
                        error: (error) => {
                            // Manejar el error
                            console.error(error);
                        }
                    });
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void
    {
        // Return if user is not available
        if ( !this.user )
        {
            return;
        }

        // Update the user
        this._userService.update({
            ...this.user,
            status,
        }).subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }
}
